// Groq Whisper API client + API key management.

const API_URL = 'https://api.groq.com/openai/v1/audio/transcriptions'
const STORAGE_KEY = 'correspond_groq_key'

export const getApiKey = () => localStorage.getItem(STORAGE_KEY) ?? ''
export const setApiKey = key => localStorage.setItem(STORAGE_KEY, key.trim())
export const hasApiKey = () => Boolean(getApiKey())

export async function testApiKey(key) {
  const res = await fetch('https://api.groq.com/openai/v1/models', {
    headers: { Authorization: `Bearer ${key}` },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error?.message ?? `HTTP ${res.status}`)
  }
}

// Transcribe an audio Blob using Groq Whisper.
// contextPrompt: the section/question text — passed to Whisper's `prompt`
// parameter so it uses the right vocabulary and spellings.
export async function transcribe(audioBlob, contextPrompt = '') {
  const key = getApiKey()
  if (!key) throw new Error('No Groq API key — open Settings to add one')

  const form = new FormData()
  // Groq requires a filename with an appropriate extension
  const ext = audioBlob.type.includes('mp4') ? 'm4a'
             : audioBlob.type.includes('ogg') ? 'ogg'
             : 'webm'
  form.append('file', audioBlob, `audio.${ext}`)
  form.append('model', 'whisper-large-v3-turbo')
  form.append('response_format', 'json')
  form.append('language', 'en')
  if (contextPrompt) form.append('prompt', contextPrompt.slice(0, 500))

  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${key}` },
    body: form,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error?.message ?? `Groq API error ${res.status}`)
  }

  const data = await res.json()
  return (data.text ?? '').trim()
}

// Build the context prompt to pass to Whisper for a given section.
export function buildContext(section) {
  if (!section) return ''
  switch (section.type) {
    case 'text':
      return `Section: ${section.title}. ${section.content.slice(0, 300)}`
    case 'media':
      return `Image: ${section.title}. ${section.caption ?? ''}`
    case 'question':
      if (section.kind === 'multiple_choice') {
        const opts = (section.options ?? [])
          .map(o => `${o.id.toUpperCase()}: ${o.text}`)
          .join(', ')
        return `Question: ${section.prompt}. Options: ${opts}`
      }
      return `Question: ${section.prompt}`
    default:
      return ''
  }
}
